﻿package org.zq.pixelset.processing

import org.zq.pixelset.model.DecodedImage
import org.zq.pixelset.model.ScaleMode

object ImageResize {

    fun resize(source: DecodedImage, targetWidth: Int, targetHeight: Int, mode: ScaleMode): DecodedImage {
        val tw = targetWidth.coerceAtLeast(1)
        val th = targetHeight.coerceAtLeast(1)
        return when (mode) {
            ScaleMode.Stretch -> scaleBilinear(source, tw, th)
            ScaleMode.Fit -> fitWithPadding(source, tw, th)
            ScaleMode.Fill -> fillWithCrop(source, tw, th)
        }
    }

    private fun fitWithPadding(source: DecodedImage, tw: Int, th: Int): DecodedImage {
        val scale = minOf(tw.toFloat() / source.width, th.toFloat() / source.height)
        val sw = (source.width * scale).toInt().coerceAtLeast(1)
        val sh = (source.height * scale).toInt().coerceAtLeast(1)
        val scaled = scaleBilinear(source, sw, sh)
        val output = IntArray(tw * th) { 0xFFFFFFFF.toInt() }
        val ox = (tw - sw) / 2
        val oy = (th - sh) / 2
        blit(scaled, output, tw, ox, oy)
        return DecodedImage(tw, th, output)
    }

    private fun fillWithCrop(source: DecodedImage, tw: Int, th: Int): DecodedImage {
        val scale = maxOf(tw.toFloat() / source.width, th.toFloat() / source.height)
        val sw = (source.width * scale).toInt().coerceAtLeast(1)
        val sh = (source.height * scale).toInt().coerceAtLeast(1)
        val scaled = scaleBilinear(source, sw, sh)
        val cx = (sw - tw) / 2
        val cy = (sh - th) / 2
        val output = IntArray(tw * th)
        for (y in 0 until th) {
            for (x in 0 until tw) {
                output[y * tw + x] = scaled.pixels[(cy + y) * sw + (cx + x)]
            }
        }
        return DecodedImage(tw, th, output)
    }

    private fun blit(source: DecodedImage, dest: IntArray, destWidth: Int, offsetX: Int, offsetY: Int) {
        for (y in 0 until source.height) {
            for (x in 0 until source.width) {
                val dx = offsetX + x
                val dy = offsetY + y
                if (dx in 0 until destWidth && dy * destWidth + dx < dest.size) {
                    dest[dy * destWidth + dx] = source.pixels[y * source.width + x]
                }
            }
        }
    }

    fun scaleBilinear(source: DecodedImage, targetWidth: Int, targetHeight: Int): DecodedImage {
        val tw = targetWidth.coerceAtLeast(1)
        val th = targetHeight.coerceAtLeast(1)
        if (source.width == tw && source.height == th) return source
        val output = IntArray(tw * th)
        val xRatio = source.width.toFloat() / tw
        val yRatio = source.height.toFloat() / th
        for (y in 0 until th) {
            for (x in 0 until tw) {
                val srcX = x * xRatio
                val srcY = y * yRatio
                val x0 = srcX.toInt().coerceIn(0, source.width - 1)
                val y0 = srcY.toInt().coerceIn(0, source.height - 1)
                val x1 = (x0 + 1).coerceAtMost(source.width - 1)
                val y1 = (y0 + 1).coerceAtMost(source.height - 1)
                val xWeight = srcX - x0
                val yWeight = srcY - y0
                output[y * tw + x] = lerpColor(
                    lerpColor(pixelAt(source, x0, y0), pixelAt(source, x1, y0), xWeight),
                    lerpColor(pixelAt(source, x0, y1), pixelAt(source, x1, y1), xWeight),
                    yWeight,
                )
            }
        }
        return DecodedImage(tw, th, output)
    }

    private fun pixelAt(image: DecodedImage, x: Int, y: Int): Int = image.pixels[y * image.width + x]

    private fun lerpColor(c0: Int, c1: Int, t: Float): Int {
        val a = lerpChannel(c0, c1, 24, t)
        val r = lerpChannel(c0, c1, 16, t)
        val g = lerpChannel(c0, c1, 8, t)
        val b = lerpChannel(c0, c1, 0, t)
        return (a shl 24) or (r shl 16) or (g shl 8) or b
    }

    private fun lerpChannel(c0: Int, c1: Int, shift: Int, t: Float): Int {
        val v0 = (c0 shr shift) and 0xFF
        val v1 = (c1 shr shift) and 0xFF
        return (v0 + (v1 - v0) * t).toInt().coerceIn(0, 255)
    }
}
